<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import glossaryAPI from 'dashboard/api/glossary';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  glossaries: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'created']);
const { t } = useI18n();

const dialogRef = ref(null);
const isSaving = ref(false);

const form = reactive({
  name: '',
  source_language: 'zh',
  target_language: '',
  copy_from_glossary_id: '',
});

const selectedSourceGlossary = computed(() =>
  props.glossaries.find(
    glossary => String(glossary.id) === form.copy_from_glossary_id
  )
);

const termsToCopy = computed(() =>
  (selectedSourceGlossary.value?.terms || [])
    .filter(term => term.source_term?.trim() && term.target_term?.trim())
    .map(term => ({
      source_term: term.source_term.trim(),
      target_term: term.target_term.trim(),
    }))
);

const isConfirmDisabled = computed(
  () =>
    !form.name.trim() ||
    !form.source_language.trim() ||
    !form.target_language.trim()
);

const resetForm = () => {
  form.name = '';
  form.source_language = 'zh';
  form.target_language = '';
  form.copy_from_glossary_id = '';
};

const alertForLocalFirstSave = resource => {
  if (resource?.status === 'failed') {
    useAlert(t('GLOSSARY_SETTINGS.MESSAGES.CREATE_SYNC_FAILED'));
    return;
  }

  useAlert(t('GLOSSARY_SETTINGS.MESSAGES.CREATE_SUCCESS'));
};

const handleSubmit = async () => {
  if (isConfirmDisabled.value) return;

  isSaving.value = true;
  try {
    const response = await glossaryAPI.create({
      glossary: {
        name: form.name.trim(),
        source_language: form.source_language.trim(),
        target_language: form.target_language.trim(),
        terms: termsToCopy.value,
      },
    });

    alertForLocalFirstSave(response.data);
    emit('created', response.data);
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('GLOSSARY_SETTINGS.MESSAGES.SAVE_FAILED')
    );
  } finally {
    isSaving.value = false;
  }
};

const handleClose = () => {
  resetForm();
  emit('close');
};

const open = () => {
  dialogRef.value?.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t('GLOSSARY_SETTINGS.CREATE_TITLE')"
    :description="$t('GLOSSARY_SETTINGS.CREATE_DESCRIPTION')"
    :confirm-button-label="$t('GLOSSARY_SETTINGS.ACTIONS.CREATE')"
    :disable-confirm-button="isConfirmDisabled"
    :is-loading="isSaving"
    @confirm="handleSubmit"
    @close="handleClose"
  >
    <div class="grid gap-3">
      <input
        v-model="form.name"
        type="text"
        class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12"
        :placeholder="$t('GLOSSARY_SETTINGS.FORM.NAME')"
      />
      <div class="grid grid-cols-2 gap-3">
        <input
          v-model="form.source_language"
          type="text"
          class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm uppercase text-n-slate-12"
          :placeholder="$t('GLOSSARY_SETTINGS.FORM.SOURCE_LANGUAGE')"
        />
        <input
          v-model="form.target_language"
          type="text"
          class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm uppercase text-n-slate-12"
          :placeholder="$t('GLOSSARY_SETTINGS.FORM.TARGET_LANGUAGE')"
        />
      </div>
      <div class="grid gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('GLOSSARY_SETTINGS.FORM.COPY_TERMS_FROM') }}
        </label>
        <select
          v-model="form.copy_from_glossary_id"
          class="h-10 w-full rounded-lg border border-n-weak bg-n-background px-3 py-0 text-sm leading-10 text-n-slate-12"
        >
          <option value="">
            {{ $t('GLOSSARY_SETTINGS.FORM.COPY_TERMS_NONE') }}
          </option>
          <option
            v-for="glossary in glossaries"
            :key="glossary.id"
            :value="String(glossary.id)"
          >
            {{ glossary.name }}
          </option>
        </select>
      </div>
    </div>
  </Dialog>
</template>
